import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, BookOpen, Dumbbell, MessageCircle, NotebookPen, Library, LogOut, Leaf, Bell, BellOff, Send, Crown, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { t, languages } from "@/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Toaster } from "sonner";
import { subscribePush, setReminderHour as apiSetReminderHour, sendTestPush } from "@/lib/push";

const nav = [
  { to: "/app", icon: Home, key: "dashboard", end: true },
  { to: "/app/study", icon: BookOpen, key: "study" },
  { to: "/app/exercises", icon: Dumbbell, key: "exercises" },
  { to: "/app/practice", icon: MessageCircle, key: "practice" },
  { to: "/app/journal", icon: NotebookPen, key: "journal" },
  { to: "/app/library", icon: Library, key: "library" },
  { to: "/app/plan", icon: Crown, key: "plan" },
];

// Compute UTC offset in hours for a given IANA timezone (positive means tz is behind UTC).
function tzOffsetToUTC(tz) {
  try {
    const now = new Date();
    // Format the local wall-clock time in tz
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const parts = Object.fromEntries(dtf.formatToParts(now).filter(p => p.type !== "literal").map(p => [p.type, p.value]));
    const localAsUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    return Math.round((localAsUtc - now.getTime()) / 3600000); // hours to add to UTC to get local wall clock
  } catch (e) {
    console.warn("tz offset calc failed", e?.message || e);
    return 0;
  }
}
const localToUtcHour = (localHour, tz) => ((localHour - tzOffsetToUTC(tz)) % 24 + 24) % 24;
const utcToLocalHour = (utcHour, tz) => ((utcHour + tzOffsetToUTC(tz)) % 24 + 24) % 24;

export default function Layout() {
  const { user, logout } = useAuth();
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const tz = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const [notifPerm, setNotifPerm] = React.useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [localHour, setLocalHour] = React.useState(-1);

  useEffect(() => {
    if (user?.reminder_hour !== undefined && user.reminder_hour !== -1) {
      setLocalHour(utcToLocalHour(user.reminder_hour, tz));
    } else {
      setLocalHour(-1);
    }
    // utcToLocalHour is a pure module-level helper; tz + reminder_hour are the real deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.reminder_hour, tz]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") { toast.error("Unsupported"); return; }
    const perm = await Notification.requestPermission();
    setNotifPerm(perm);
    if (perm !== "granted") { toast.error(t(lang, "notifications_denied")); return; }
    try {
      await subscribePush();
      toast.success(t(lang, "notifications_enabled"));
      localStorage.setItem("nvc_notif_enabled", "1");
    } catch (e) {
      console.warn("subscribePush failed", e?.message || e);
      toast.error(t(lang, "error"));
    }
  };

  const changeLocalHour = async (v) => {
    const h = parseInt(v, 10);
    setLocalHour(h);
    const utcH = h === -1 ? -1 : localToUtcHour(h, tz);
    try {
      await apiSetReminderHour(utcH);
    } catch (e) {
      console.warn("apiSetReminderHour failed", e?.message || e);
    }
  };

  const testPush = async () => {
    try {
      const r = await sendTestPush();
      if (r.sent > 0) toast.success(t(lang, "push_sent"));
      else toast.error(t(lang, "error"));
    } catch (e) {
      console.warn("sendTestPush failed", e?.message || e);
      toast.error(t(lang, "error"));
    }
  };

  const isPremium = user?.plan === "premium";

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <Toaster position="top-right" richColors />
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card/50 backdrop-blur-sm sticky top-0 h-screen">
        <div className="px-6 py-8">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
              <Leaf className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold leading-tight">{t(lang, "app_name")}</div>
              <div className="text-xs text-muted-foreground">{t(lang, "tagline")}</div>
            </div>
          </div>
          {isPremium && (
            <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 border border-accent/40 text-accent text-xs" data-testid="premium-badge">
              <Crown className="w-3 h-3" strokeWidth={2} /> {t(lang, "premium_plan")}
            </div>
          )}
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {nav.map(({ to, icon: Icon, key, end }) => (
            <NavLink
              key={key}
              to={to}
              end={end}
              data-testid={`nav-${key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-[background-color,color] ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{t(lang, key)}</span>
              {key === "plan" && !isPremium && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-accent text-accent-foreground">PRO</span>
              )}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/app/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-[background-color,color] ${
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <Shield className="w-4 h-4" strokeWidth={1.5} />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-border space-y-3">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger data-testid="lang-select" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <Button
            data-testid="notif-toggle"
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={enableNotifications}
          >
            {notifPerm === "granted" ? <Bell className="w-4 h-4" strokeWidth={1.5} /> : <BellOff className="w-4 h-4" strokeWidth={1.5} />}
            {notifPerm === "granted" ? t(lang, "notifications_enabled") : t(lang, "enable_notifications")}
          </Button>
          {notifPerm === "granted" && (
            <>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">{t(lang, "reminder_hour")} · {tz}</div>
                <Select value={String(localHour)} onValueChange={changeLocalHour}>
                  <SelectTrigger data-testid="reminder-hour" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="-1">{t(lang, "reminder_off")}</SelectItem>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={`hour-${i}`} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="test-push-btn"
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2"
                onClick={testPush}
              >
                <Send className="w-4 h-4" strokeWidth={1.5} /> {t(lang, "send_test_push")}
              </Button>
            </>
          )}
          <Button
            data-testid="logout-btn"
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={doLogout}
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            {t(lang, "logout")}
          </Button>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/90 backdrop-blur">
        <div className="flex justify-around py-2 overflow-x-auto">
          {nav.map(({ to, icon: Icon, key, end }) => (
            <NavLink
              key={key}
              to={to}
              end={end}
              data-testid={`mnav-${key}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-2 py-1 text-xs ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{t(lang, key)}</span>
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 min-w-0 pb-20 md:pb-0">
        <motion.div
          key={window.location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-5xl mx-auto px-6 md:px-10 py-10"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}
